
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { LucideIcon } from 'lucide-react';
import { CampaignSettings } from './types';
import CampaignSubTypeSelector from './CampaignSubTypeSelector';
import CampaignSettingsForm from './CampaignSettingsForm';
import CampaignHeader from './setup/CampaignHeader';
import CampaignPreview from './setup/CampaignPreview';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { generateCampaignId } from '@/utils/campaignUtils';

interface CampaignData {
  id: string;
  title: string;
  icon: LucideIcon;
  description: string;
  setupSteps: string[];
}

interface CampaignSetupProps {
  campaignType: string;
  campaignData?: CampaignData;
}

const CampaignSetup: React.FC<CampaignSetupProps> = ({ 
  campaignType,
  campaignData 
}) => {
  const [activeTab, setActiveTab] = useState('type');
  const [campaignSettings, setCampaignSettings] = useState<CampaignSettings>(() => {
    // Default settings based on campaign type
    if (campaignType === 'affiliate-program') {
      return {
        subType: 'single' as const,
        title: "Join Our Affiliate Program",
        subtitle: "Earn 10% commission on every sale you refer to our platform.",
        referrerReward: 0, // Not applicable for affiliate program
        refereeReward: 0, // Not applicable for affiliate program
        currency: "USD",
        shareOptions: ["whatsapp", "instagram", "facebook", "linkedin", "twitter", "mail"],
        showAffiliatePortal: true,
        customMessages: {},
        collectPhoneNumber: true,
        limitReferrals: false,
        showTerms: true,
        termsContent: "By joining our affiliate program, you agree to our terms and conditions...",
        programDescription: "Our affiliate program rewards partners for promoting our products.",
        rewardInfo: "Earn 10% commission on all referred sales",
        isActive: false,
        campaignId: `campaign-${Date.now()}`,
        globalCampaignId: generateCampaignId(),
        createdAt: new Date(),
        updatedAt: new Date(),
        affiliateSettings: {
          commissionType: 'percentage',
          commissionRate: 10,
          minimumPayout: 50,
          payoutMethod: 'paypal',
          payoutFrequency: 'monthly',
          trackingParameters: ['ref', 'aff'],
          marketingAssets: [],
          salesTracking: true,
          autoEmailSummary: true
        }
      };
    } else {
      // Default for regular referral campaigns
      return {
        subType: 'double' as const,
        title: "Give $20, Get $20",
        subtitle: "Invite friends and you'll both receive $20 when they make their first purchase.",
        referrerReward: 20,
        refereeReward: 20,
        currency: "USD",
        shareOptions: ["whatsapp", "instagram", "facebook", "linkedin", "twitter", "mail"],
        showAffiliatePortal: true,
        customMessages: {
          whatsapp: "Your friend thinks you will love us and sent a little something nice.",
          linkedin: "Check this out! Your friend thinks you will love us and sent a little something nice."
        },
        collectPhoneNumber: false,
        limitReferrals: false,
        showTerms: false,
        termsContent: "",
        programDescription: "",
        rewardInfo: "",
        isActive: false,
        campaignId: `campaign-${Date.now()}`,
        globalCampaignId: generateCampaignId(),
        createdAt: new Date(),
        updatedAt: new Date()
      };
    }
  });
  
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSettingsChange = (newSettings: Partial<CampaignSettings>) => {
    setCampaignSettings({
      ...campaignSettings,
      ...newSettings,
      updatedAt: new Date()
    });
  };

  const handleSubTypeChange = (subType: 'single' | 'double') => {
    // For affiliate programs, subType doesn't control the reward structure
    if (campaignType === 'affiliate-program') {
      setCampaignSettings({
        ...campaignSettings,
        subType,
        updatedAt: new Date()
      });
      return;
    }
    
    // For regular referral campaigns
    setCampaignSettings({
      ...campaignSettings,
      subType,
      title: subType === 'single' ? "Get $20 for Referring Friends" : "Give $20, Get $20",
      subtitle: subType === 'single' 
        ? "Invite friends and get $20 when they make their first purchase."
        : "Invite friends and you'll both receive $20 when they make their first purchase.",
      refereeReward: subType === 'single' ? 0 : 20,
      updatedAt: new Date()
    });
  };

  const handleNextStep = () => {
    if (activeTab === 'type') {
      setActiveTab('settings');
    } else if (activeTab === 'settings') {
      setActiveTab('preview');
    }
  };

  const handleActivateCampaign = (settings: CampaignSettings) => {
    // In a real application, you would save the campaign to your database
    const activatedSettings = {
      ...settings,
      isActive: true,
      updatedAt: new Date()
    };
    
    // For demonstration, just show toast and redirect to active campaigns
    toast({
      title: "Campaign Activated Successfully",
      description: campaignType === 'affiliate-program'
        ? `Your affiliate program is now live with ID ${settings.globalCampaignId}.`
        : `Your ${settings.subType === 'double' ? 'double-sided' : 'single-sided'} referral campaign is now live with ID ${settings.globalCampaignId}.`,
    });
    
    navigate('/admin/campaigns', { state: { activatedCampaign: activatedSettings } });
  };

  // Determine if we should show the campaign subtype selector
  // For affiliate programs, we can skip this step
  const skipSubtypeSelection = campaignType === 'affiliate-program';
  
  // If skipping subtype, start directly at settings
  React.useEffect(() => {
    if (skipSubtypeSelection && activeTab === 'type') {
      setActiveTab('settings');
    }
  }, [skipSubtypeSelection, activeTab]);

  return (
    <div className="space-y-6">
      <Card>
        <CampaignHeader 
          title={`Set Up: ${campaignData ? campaignData.title : "Campaign Setup"}`}
          icon={campaignData?.icon}
          description={
            campaignType === 'affiliate-program' 
              ? "Configure your affiliate program settings" 
              : "Follow the steps below to configure your campaign"
          }
        />
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="type" disabled={skipSubtypeSelection}>
                {campaignType === 'affiliate-program' ? 'Program Type' : 'Campaign Type'}
              </TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
              <TabsTrigger value="preview">Preview</TabsTrigger>
            </TabsList>
            
            {!skipSubtypeSelection && (
              <TabsContent value="type">
                <CampaignSubTypeSelector 
                  selectedType={campaignSettings.subType}
                  onTypeSelect={handleSubTypeChange}
                  onNext={handleNextStep}
                />
              </TabsContent>
            )}
            
            <TabsContent value="settings">
              <CampaignSettingsForm 
                settings={campaignSettings}
                onChange={handleSettingsChange}
                campaignType={campaignType}
              />
              <div className="flex justify-end mt-6">
                <Button onClick={handleNextStep} size="lg">
                  Next: Preview Campaign
                </Button>
              </div>
            </TabsContent>
            
            <TabsContent value="preview">
              <CampaignPreview 
                settings={campaignSettings} 
                onActivate={handleActivateCampaign}
                campaignType={campaignType}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default CampaignSetup;
